import { useEffect, useRef, type MutableRefObject, type RefObject } from 'react'
import { scheduleScrollAfterLayoutSettle } from '../../../util/scroll'

const TARGET_POLL_INTERVAL_MS = 50
const MAX_TARGET_POLL_ATTEMPTS = 30

export interface DeepLinkAccountScrollArgs {
  /** True while account nodes are not mounted (fetch, init, or tenant-id mismatch). */
  loading: boolean
  tenantId?: string
  /** Resolved king account number for the deep-link target (query may be a utility SAN). */
  kingAccountNumber?: string
  accountRefs: MutableRefObject<Record<string, RefObject<HTMLDivElement>>>
  /** Page header + accounts toolbar height, read when the scroll runs. */
  stickyOffset: number
}

/**
 * Scroll to the deep-linked account once per landing (tenant + account number).
 * Tenant refreshes rebuild `serviceAccounts`, panels open and close, and the sticky
 * offset re-measures — re-running on any of those is what dragged the page back to
 * the searched account (WEB-7239). The landing is therefore consumed by a ref guard
 * and the sticky offset is read from a ref instead of being an effect dependency.
 */
export const useDeepLinkAccountScroll = ({
  loading,
  tenantId,
  kingAccountNumber,
  accountRefs,
  stickyOffset,
}: DeepLinkAccountScrollArgs): void => {
  const stickyOffsetRef = useRef(stickyOffset)
  const scrolledLandingKeyRef = useRef<string | null>(null)
  const cancelScrollRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    stickyOffsetRef.current = stickyOffset
  }, [stickyOffset])

  // Cancel on unmount only. Cancelling on every re-render would abort the landing
  // scroll that the guard has already marked as spent.
  useEffect(() => {
    return () => {
      cancelScrollRef.current?.()
      cancelScrollRef.current = null
    }
  }, [])

  const landingKey = kingAccountNumber != null ? `${tenantId ?? ''}|${kingAccountNumber}` : null

  useEffect(() => {
    if (landingKey == null || kingAccountNumber == null) {
      // Deep link gone: stop an in-flight settle so opening another account
      // cannot yank the viewport back, then allow a later search to scroll again.
      scrolledLandingKeyRef.current = null
      cancelScrollRef.current?.()
      cancelScrollRef.current = null
      return
    }
    if (loading) return
    if (scrolledLandingKeyRef.current === landingKey) return

    cancelScrollRef.current?.()
    cancelScrollRef.current = null

    let cancelled = false
    let attempts = 0
    let pollTimeout = 0

    const tryScroll = (): void => {
      if (cancelled) return
      const el = accountRefs.current[kingAccountNumber]?.current
      if (el != null) {
        const accountsRoot = el.closest('[data-tenant-accounts]')
        cancelScrollRef.current = scheduleScrollAfterLayoutSettle(
          el,
          stickyOffsetRef.current,
          accountsRoot instanceof HTMLElement ? accountsRoot : el,
          { settleMs: 250, maxWaitMs: 3000, correctAfterMs: 400 }
        )
        scrolledLandingKeyRef.current = landingKey
        return
      }
      if (attempts++ < MAX_TARGET_POLL_ATTEMPTS) {
        pollTimeout = window.setTimeout(tryScroll, TARGET_POLL_INTERVAL_MS)
      }
    }

    pollTimeout = window.setTimeout(tryScroll, TARGET_POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      window.clearTimeout(pollTimeout)
      // Leave an already-scheduled settle running. Cancelling here aborts the
      // landing on React Strict Mode remount once the guard is set, and a
      // loading flicker would then never retry. Unmount still cancels.
    }
  }, [accountRefs, kingAccountNumber, landingKey, loading])
}

export default useDeepLinkAccountScroll
